import React, { useState, useEffect, useMemo } from 'react';
import { fuzzySearch } from '../../utils/fuzzySearch';
import { FaEdit, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import styles from './styles/ProductCrud.module.css';
import shared from './styles/adminShared.module.css';
import PrimaryButton from '../PrimaryButton';
import { getProductImageUrls } from '../../utils';

// Los datos de tienda, venta y etiqueta comparten la misma fila de Productos.
interface Product {
    idProductos: number;
    NombresProductos: string;   // Nombre de tienda (largo, libre)
    NombreEtiqueta?: string;
    NombreEtiquetaFreezer?: string;
    Precio: number;
    descripcion: string;
    descripcionProducto: string;
    IngredientesSugeridos: string;
    SugerenciasCoccion: string;
    marca: string;
    valor_propiedad_1: string;
    nombre_propiedad_1: string;
    tags: string;
    seo_descripcion: string;
    nombre_subcategoria?: string;
    nombre_categoria?: string;
    idSubcategoria?: number;
    barcode?: string;
    CodigoBarras?: number | string | null;
    PorcionTexto?: string;
    EsUltracongelado?: number | boolean;
    EsEnvasadoAlVacio?: number | boolean;
    InstruccionesConservacion?: string;
    TieneSugerenciasCoccion?: number | boolean;
    DiasVencimientoDefault?: number | null;
    slug?: string;
    UrlImagen?: string;
    UrlImagen2?: string;
    UrlImagen3?: string;
    producto_padre_id?: number | null;
    es_promocion?: number | boolean;
    activo?: number | boolean;
}

interface Subcategory {
    idSubcategoria: number;
    nombre: string;
}

interface NewVariant {
    tempId: string;
    NombresProductos: string;
    Precio: string;
    UrlImagen: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const IMAGE_SIZE_HELP = 'Ideal: imagen cuadrada 1200 x 1200 px, formato JPG/PNG/WEBP y menor a 1 MB.';

const getApiErrorMessage = async (res: Response, fallback: string) => {
    try {
        const data = await res.json();
        return data?.error || data?.message || fallback;
    } catch {
        return fallback;
    }
};

const ProductCrud: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [view, setView] = useState<'create' | 'list'>('create');

    const initialForm = {
        idProductos: 0,
        NombresProductos: '',
        NombreEtiqueta: '',
        NombreEtiquetaFreezer: '',
        Precio: 0,
        descripcion: '', // Descripcion corta (unused in UI but kept for DB)
        descripcionProducto: '', // Descripción para la tienda web
        IngredientesSugeridos: '',
        SugerenciasCoccion: '',
        marca: '',
        valor_propiedad_1: '',
        nombre_propiedad_1: '',
        tags: '',
        seo_descripcion: '',
        idSubcategoria: 0,
        barcode: '',
        CodigoBarras: '',
        PorcionTexto: '',
        EsUltracongelado: true,
        EsEnvasadoAlVacio: false,
        InstruccionesConservacion: '',
        TieneSugerenciasCoccion: false,
        DiasVencimientoDefault: '',
        slug: '',
        UrlImagen: '',
        UrlImagen2: '',
        UrlImagen3: '',
        producto_padre_id: 0,
        activo: true
    };

    const [formData, setFormData] = useState(initialForm);
    const [isEditing, setIsEditing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [parentSearchTerm, setParentSearchTerm] = useState('');
    const [failedPreviews, setFailedPreviews] = useState<Record<string, boolean>>({});
    const [newVariants, setNewVariants] = useState<NewVariant[]>([]);
    const [failedVariantPreviews, setFailedVariantPreviews] = useState<Record<string, boolean>>({});

    const imagePreviewUrls = useMemo(() => getProductImageUrls({
        UrlImagen: formData.UrlImagen,
        UrlImagen2: formData.UrlImagen2,
        UrlImagen3: formData.UrlImagen3
    }), [formData.UrlImagen, formData.UrlImagen2, formData.UrlImagen3]);

    useEffect(() => {
        setFailedPreviews({});
    }, [formData.UrlImagen, formData.UrlImagen2, formData.UrlImagen3]);

    useEffect(() => {
        fetchProducts();
        fetchSubcategories();
    }, []);

    const filteredProducts = useMemo(() => {
        return fuzzySearch(products, searchTerm, {
            keys: ['NombresProductos', 'nombre_subcategoria'],
            threshold: 0.35
        });
    }, [products, searchTerm]);

    const productNameById = (id?: number | null) =>
        products.find(product => product.idProductos === Number(id))?.NombresProductos || '';

    const formatPrice = (price: number) => new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 0
    }).format(Number(price || 0));

    const parentCandidates = useMemo(() => {
        const candidates = products.filter(product =>
            product.idProductos !== formData.idProductos &&
            !product.es_promocion &&
            !product.producto_padre_id
        );

        if (!parentSearchTerm.trim()) return candidates.slice(0, 8);

        return fuzzySearch(candidates, parentSearchTerm, {
            keys: ['NombresProductos', 'nombre_subcategoria', 'barcode'],
            threshold: 0.35
        }).slice(0, 8);
    }, [products, formData.idProductos, parentSearchTerm]);

    const selectParentProduct = (product: Product) => {
        setFormData({ ...formData, producto_padre_id: product.idProductos });
        setParentSearchTerm(product.NombresProductos);
        setNewVariants([]);
    };

    const clearParentProduct = () => {
        setFormData({ ...formData, producto_padre_id: 0 });
        setParentSearchTerm('');
    };

    const addVariant = () => {
        setNewVariants(current => [...current, {
            tempId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            NombresProductos: '',
            Precio: '',
            UrlImagen: ''
        }]);
    };

    const updateVariant = (tempId: string, field: keyof Omit<NewVariant, 'tempId'>, value: string) => {
        setNewVariants(current => current.map(variant =>
            variant.tempId === tempId ? { ...variant, [field]: value } : variant
        ));
        if (field === 'UrlImagen') {
            setFailedVariantPreviews(current => ({ ...current, [tempId]: false }));
        }
    };

    const removeVariant = (tempId: string) => {
        setNewVariants(current => current.filter(variant => variant.tempId !== tempId));
        setFailedVariantPreviews(current => {
            const next = { ...current };
            delete next[tempId];
            return next;
        });
    };

    const fetchProducts = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_URL}/productos?include_hidden=true&include_variants=true`);
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Error al cargar productos'));
            }
            const data = await res.json();
            if (Array.isArray(data)) {
                setProducts(data);
            } else {
                setProducts([]);
                setError('La API de productos devolvio un formato inesperado.');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al cargar productos');
        } finally {
            setLoading(false);
        }
    };

    const fetchSubcategories = async () => {
        try {
            const res = await fetch(`${API_URL}/subcategorias`);
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Error al cargar subcategorias'));
            }
            const data = await res.json();
            if (Array.isArray(data)) {
                setSubcategories(data);
            } else {
                setSubcategories([]);
                setError('La API de subcategorias devolvio un formato inesperado.');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al cargar subcategorias');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const nombre = formData.NombresProductos.trim();
        if (!nombre) {
            setError('El nombre de tienda es obligatorio');
            return;
        }

        if (!isEditing) {
            const invalidVariantIndex = newVariants.findIndex(variant =>
                !variant.NombresProductos.trim() ||
                Number(variant.Precio) <= 0 ||
                !variant.UrlImagen.trim()
            );
            if (invalidVariantIndex >= 0) {
                setError(`Completá nombre, precio e imagen de la variante ${invalidVariantIndex + 1}`);
                return;
            }
        }

        const url = isEditing
            ? `${API_URL}/admin/productos/${formData.idProductos}`
            : `${API_URL}/admin/productos`;
        const method = isEditing ? 'PUT' : 'POST';

        // Prepare info for API (convert bools to 0/1 if needed by your DB, or let backend handle)
        // SQL usually expects 0/1 for BOOLEAN if using specific drivers, but TRUE/FALSE often works.
        // Let's assume sending boolean JSON is fine for Flask/MySQL connector if handled.

        try {
            const payload = isEditing ? formData : {
                ...formData,
                variantes: newVariants.map(variant => ({
                    NombresProductos: variant.NombresProductos.trim(),
                    Precio: Number(variant.Precio),
                    UrlImagen: variant.UrlImagen.trim()
                }))
            };
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                fetchProducts();
                resetForm();
            } else {
                let msg = 'Error al guardar producto';
                try {
                    const errData = await res.json();
                    if (errData?.error) msg = errData.error;
                } catch { /* respuesta sin JSON */ }
                setError(msg);
            }
        } catch (err) {
            setError('Error de conexión');
        }
    };

    const handleEdit = (prod: Product) => {
        // Fetch full details to ensure we have all fields
        fetchProductDetail(prod.idProductos);
        setView('create');
    };

    const fetchProductDetail = async (id: number) => {
        try {
            const res = await fetch(`${API_URL}/productos/${id}`);
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Error al cargar el detalle del producto'));
            }
            const data = await res.json();
            if (data) {
                setFormData({
                    idProductos: data.idProductos,
                    NombresProductos: data.NombresProductos || '',
                    NombreEtiqueta: data.NombreEtiqueta || '',
                    NombreEtiquetaFreezer: data.NombreEtiquetaFreezer || '',
                    Precio: data.Precio || 0,
                    descripcion: data.descripcion || '',
                    descripcionProducto: data.descripcionProducto || '',
                    IngredientesSugeridos: data.IngredientesSugeridos || '',
                    SugerenciasCoccion: data.SugerenciasCoccion || '',
                    marca: data.marca || '',
                    valor_propiedad_1: data.valor_propiedad_1 || '',
                    nombre_propiedad_1: data.nombre_propiedad_1 || '',
                    tags: data.tags || '',
                    seo_descripcion: data.seo_descripcion || '',
                    idSubcategoria: data.idSubcategoria || 0,
                    barcode: data.barcode || '',
                    CodigoBarras: data.CodigoBarras ?? '',
                    PorcionTexto: data.PorcionTexto || '',
                    EsUltracongelado: data.EsUltracongelado !== undefined ? !!data.EsUltracongelado : true,
                    EsEnvasadoAlVacio: data.EsEnvasadoAlVacio !== undefined ? !!data.EsEnvasadoAlVacio : false,
                    InstruccionesConservacion: data.InstruccionesConservacion || '',
                    TieneSugerenciasCoccion: data.TieneSugerenciasCoccion !== undefined ? !!data.TieneSugerenciasCoccion : false,
                    DiasVencimientoDefault: data.DiasVencimientoDefault ?? '',
                    slug: data.slug || '',
                    UrlImagen: data.UrlImagen || '',
                    UrlImagen2: data.UrlImagen2 || '',
                    UrlImagen3: data.UrlImagen3 || '',
                    producto_padre_id: data.producto_padre_id || 0,
                    activo: data.activo !== undefined ? !!data.activo : true
                });
                setParentSearchTerm(data.producto_padre_id ? productNameById(data.producto_padre_id) : '');
                setIsEditing(true);
                window.scrollTo(0, 0);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al cargar el detalle del producto');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('¿Eliminar producto?')) return;
        try {
            const res = await fetch(`${API_URL}/admin/productos/${id}`, { method: 'DELETE' });
            if (res.ok) fetchProducts();
            else setError(await getApiErrorMessage(res, 'Error al eliminar producto'));
        } catch (err) { setError(err instanceof Error ? err.message : 'Error eliminando'); }
    };

    const resetForm = () => {
        setFormData(initialForm);
        setIsEditing(false);
        setParentSearchTerm('');
        setNewVariants([]);
        setFailedVariantPreviews({});
    };

    const renderProductGrid = () => {
        if (filteredProducts.length === 0) {
            return <div className={styles['empty-products']}>No hay productos que coincidan con la busqueda.</div>;
        }

        return (
            <div className={styles['product-admin-grid']}>
                {filteredProducts.map(p => {
                    const imageUrl = getProductImageUrls(p)[0];
                    const isActive = !!p.activo;
                    const groupLabel = p.producto_padre_id
                        ? `Variante de ${productNameById(p.producto_padre_id) || `#${p.producto_padre_id}`}`
                        : 'Principal';

                    return (
                        <article key={p.idProductos} className={`${styles['product-admin-card']} ${!isActive ? styles['is-inactive'] : ''}`}>
                            <div className={styles['product-card-image']}>
                                <div className={styles['product-card-actions']}>
                                    <button type="button" onClick={() => handleEdit(p)} className={styles['card-action-btn']} title="Editar producto" aria-label={`Editar ${p.NombresProductos}`}>
                                        <FaEdit />
                                    </button>
                                    <button type="button" onClick={() => handleDelete(p.idProductos)} className={`${styles['card-action-btn']} ${styles['delete-action']}`} title="Eliminar producto" aria-label={`Eliminar ${p.NombresProductos}`}>
                                        <FaTrash />
                                    </button>
                                </div>
                                <span className={`${styles['status-badge']} ${isActive ? styles['active'] : styles['inactive']}`}>
                                    {isActive ? 'Activo' : 'Oculto'}
                                </span>
                                {imageUrl ? (
                                    <>
                                        <img loading="lazy" decoding="async"
                                            src={imageUrl}
                                            alt={p.NombresProductos}
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                e.currentTarget.nextElementSibling?.classList.remove(styles['is-hidden']);
                                            }}
                                        />
                                        <div className={`${styles['product-card-placeholder']} ${styles['is-hidden']}`}>
                                            {p.NombresProductos.substring(0, 1)}
                                        </div>
                                    </>
                                ) : (
                                    <div className={styles['product-card-placeholder']}>
                                        {p.NombresProductos.substring(0, 1)}
                                    </div>
                                )}
                            </div>

                            <div className={styles['product-card-body']}>
                                <span className={styles['product-card-id']}>#{p.idProductos}</span>
                                <h4 title={p.NombresProductos}>{p.NombresProductos}</h4>
                                <strong className={styles['product-card-price']}>{formatPrice(p.Precio)}</strong>
                                <div className={styles['product-card-meta']}>
                                    <span>{p.nombre_subcategoria || 'Sin subcategoria'}</span>
                                    <span>{groupLabel}</span>
                                    <span className={styles['barcode']}>{p.barcode || 'Sin codigo'}</span>
                                </div>
                            </div>
                        </article>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={styles['product-crud-container']}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>Gestión de Productos</h3>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
                <button
                    onClick={() => { resetForm(); setView('create'); }}
                    style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: view === 'create' ? '#e3f2fd' : 'transparent',
                        color: view === 'create' ? '#0d47a1' : '#666',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        transition: 'all 0.2s',
                        boxShadow: view === 'create' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                    }}
                >
                    {isEditing ? 'Editar Producto' : 'Nuevo Producto'}
                </button>
                <button
                    onClick={() => { resetForm(); setView('list'); }}
                    style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: view === 'list' ? '#e3f2fd' : 'transparent',
                        color: view === 'list' ? '#0d47a1' : '#666',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        transition: 'all 0.2s',
                        boxShadow: view === 'list' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                    }}
                >
                    Lista de Productos (Editar/Eliminar)
                </button>
            </div>

            {error && <div className={styles['error-msg']}>{error}</div>}

            {view === 'create' ? (
                <div className={styles['product-crud-form']}>
                    <h4>{isEditing ? 'Editando Producto' : 'Agregar Nuevo Producto'}</h4>
                    <form onSubmit={handleSubmit}>
                        <div className={styles['form-grid']}>
                            {/* Section: Identificación */}
                            <div className={styles['section-divider']}>Identificación</div>

                            <div className={`${styles['form-group']} ${styles['span-full']}`}>
                                <label>Nombre de Tienda * <span style={{ fontWeight: 400, color: '#888' }}>(se muestra en la web)</span></label>
                                <input
                                    type="text"
                                    name="NombresProductos"
                                    value={formData.NombresProductos}
                                    onChange={handleChange}
                                    required
                                    placeholder="Ej: Caldo de Huesos de Corderos Pastoriles Libre de Antibióticos"
                                />
                            </div>

                            <div className={styles['form-group']}>
                                <label>Nombre en Etiqueta / Pedidos</label>
                                <input
                                    type="text"
                                    name="NombreEtiqueta"
                                    value={formData.NombreEtiqueta}
                                    onChange={handleChange}
                                    maxLength={100}
                                    placeholder="Si se deja vacío usa el nombre de tienda"
                                />
                                <small style={{ color: '#999', fontSize: '0.78rem' }}>
                                    Hasta 100 caracteres: {formData.NombreEtiqueta.length}/100
                                </small>
                            </div>

                            <div className={styles['form-group']}>
                                <label>Subcategoría *</label>
                                <select name="idSubcategoria" value={formData.idSubcategoria} onChange={handleChange} required>
                                    <option value={0}>-- Seleccionar --</option>
                                    {subcategories.map(s => <option key={s.idSubcategoria} value={s.idSubcategoria}>{s.nombre}</option>)}
                                </select>
                            </div>

                            {/* Section: Venta */}
                            <div className={styles['section-divider']}>Venta</div>

                            <div className={styles['form-group']}>
                                <label>Precio *</label>
                                <input
                                    type="number"
                                    name="Precio"
                                    value={formData.Precio}
                                    onChange={handleChange}
                                    required
                                    placeholder="0.00"
                                />
                            </div>

                            <div className={styles['form-group']}>
                                <label>Código de Barras <span style={{ fontWeight: 400, color: '#888' }}>(referencia)</span></label>
                                <input
                                    type="text"
                                    value={formData.barcode || '—'}
                                    readOnly
                                    style={{ background: '#e9ecef', color: '#666', cursor: 'not-allowed' }}
                                />
                            </div>

                            {/* Section: Contenido web */}
                            <div className={styles['section-divider']}>Contenido web</div>

                            <div className={`${styles['form-group']} ${styles['span-full']}`}>
                                <label>Imágenes (URL Google Drive / Nube)</label>
                                <input type="text" name="UrlImagen" value={formData.UrlImagen} onChange={handleChange} placeholder="https://... (varias separadas por coma)" />
                                <small style={{ color: '#999', fontSize: '0.78rem' }}>{IMAGE_SIZE_HELP}</small>
                                <small style={{ color: '#999', fontSize: '0.78rem' }}>
                                    Podés poner varias imágenes separadas por coma. La primera es la principal.
                                </small>
                            </div>

                            <div className={styles['form-group']}>
                                <label>Imagen 2</label>
                                <input type="text" name="UrlImagen2" value={formData.UrlImagen2} onChange={handleChange} placeholder="https://..." />
                                <small style={{ color: '#999', fontSize: '0.78rem' }}>{IMAGE_SIZE_HELP}</small>
                            </div>

                            <div className={styles['form-group']}>
                                <label>Imagen 3</label>
                                <input type="text" name="UrlImagen3" value={formData.UrlImagen3} onChange={handleChange} placeholder="https://..." />
                                <small style={{ color: '#999', fontSize: '0.78rem' }}>{IMAGE_SIZE_HELP}</small>
                            </div>

                            {imagePreviewUrls.length > 0 && (
                                <section className={`${styles['image-preview-section']} ${styles['span-full']}`} aria-live="polite">
                                    <div className={styles['image-preview-heading']}>
                                        <strong>Previsualización</strong>
                                        <span>{imagePreviewUrls.length} imagen(es) · la primera será la principal</span>
                                    </div>
                                    <div className={styles['image-preview-grid']}>
                                        {imagePreviewUrls.map((url, index) => (
                                            <article className={styles['image-preview-card']} key={`${url}-${index}`}>
                                                <span className={styles['image-preview-order']}>
                                                    {index === 0 ? 'Principal' : `Imagen ${index + 1}`}
                                                </span>
                                                {failedPreviews[url] ? (
                                                    <div className={styles['image-preview-error']}>
                                                        No se pudo cargar esta imagen. Revisá que el enlace sea público y directo.
                                                    </div>
                                                ) : (
                                                    <img loading="lazy" decoding="async"
                                                        src={url}
                                                        alt={`Previsualización ${index + 1} de ${formData.NombresProductos || 'producto nuevo'}`}
                                                        onError={() => setFailedPreviews(current => ({ ...current, [url]: true }))}
                                                    />
                                                )}
                                            </article>
                                        ))}
                                    </div>
                                </section>
                            )}

                            <div className={`${styles['form-group']} ${styles['span-full']}`}>
                                <label>Agrupar como variante de</label>
                                <div className={styles['parent-search-box']}>
                                    <input
                                        type="text"
                                        value={parentSearchTerm}
                                        onChange={(e) => setParentSearchTerm(e.target.value)}
                                        placeholder="Buscar producto principal..."
                                    />
                                    {formData.producto_padre_id ? (
                                        <div className={styles['selected-parent']}>
                                            <span>Seleccionado: {productNameById(formData.producto_padre_id) || parentSearchTerm || `Producto #${formData.producto_padre_id}`}</span>
                                            <button type="button" onClick={clearParentProduct}>Quitar</button>
                                        </div>
                                    ) : (
                                        <div className={styles['selected-parent']}>
                                            <span>Producto principal / sin grupo</span>
                                        </div>
                                    )}
                                    {parentSearchTerm.trim() && !formData.producto_padre_id && (
                                        <div className={styles['parent-search-results']}>
                                            {parentCandidates.length > 0 ? parentCandidates.map(product => (
                                                <button
                                                    type="button"
                                                    key={product.idProductos}
                                                    className={styles['parent-search-item']}
                                                    onClick={() => selectParentProduct(product)}
                                                >
                                                    <span>{product.NombresProductos}</span>
                                                    <small>{product.nombre_subcategoria || 'Sin subcategoria'}</small>
                                                </button>
                                            )) : (
                                                <div className={styles['parent-search-empty']}>Sin resultados</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <small style={{ color: '#999', fontSize: '0.78rem' }}>
                                    La variante conserva su codigo, precio y stock; en ecommerce se muestra dentro del producto principal.
                                </small>
                            </div>

                            {!isEditing && !formData.producto_padre_id && (
                                <section className={`${styles['variants-builder']} ${styles['span-full']}`}>
                                    <div className={styles['variants-builder-heading']}>
                                        <div>
                                            <strong>Variantes del producto</strong>
                                            <span>Crealas junto al producto principal. Cada una tendrá precio, imagen, código y stock propios.</span>
                                        </div>
                                        <button type="button" onClick={addVariant} className={styles['add-variant-btn']}>
                                            <FaPlus /> Agregar variante
                                        </button>
                                    </div>

                                    {newVariants.length === 0 ? (
                                        <p className={styles['variants-empty']}>
                                            Este producto se creará sin variantes. Podés agregar todas las que necesites.
                                        </p>
                                    ) : (
                                        <div className={styles['variants-list']}>
                                            {newVariants.map((variant, index) => {
                                                const previewUrl = getProductImageUrls({ UrlImagen: variant.UrlImagen })[0];
                                                return (
                                                    <article className={styles['variant-editor-card']} key={variant.tempId}>
                                                        <div className={styles['variant-editor-title']}>
                                                            <strong>Variante {index + 1}</strong>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeVariant(variant.tempId)}
                                                                aria-label={`Quitar variante ${index + 1}`}
                                                            >
                                                                <FaTimes /> Quitar
                                                            </button>
                                                        </div>
                                                        <div className={styles['variant-fields']}>
                                                            <label>
                                                                <span>Nombre *</span>
                                                                <input
                                                                    type="text"
                                                                    value={variant.NombresProductos}
                                                                    onChange={event => updateVariant(variant.tempId, 'NombresProductos', event.target.value)}
                                                                    placeholder="Ej.: Caja de 12 unidades"
                                                                    required
                                                                />
                                                            </label>
                                                            <label>
                                                                <span>Precio *</span>
                                                                <input
                                                                    type="number"
                                                                    min="0.01"
                                                                    step="0.01"
                                                                    value={variant.Precio}
                                                                    onChange={event => updateVariant(variant.tempId, 'Precio', event.target.value)}
                                                                    placeholder="0.00"
                                                                    required
                                                                />
                                                            </label>
                                                            <label className={styles['variant-image-field']}>
                                                                <span>Imagen (URL) *</span>
                                                                <input
                                                                    type="text"
                                                                    value={variant.UrlImagen}
                                                                    onChange={event => updateVariant(variant.tempId, 'UrlImagen', event.target.value)}
                                                                    placeholder="https://..."
                                                                    required
                                                                />
                                                            </label>
                                                        </div>

                                                        {previewUrl && (
                                                            <div className={styles['variant-image-preview']}>
                                                                {failedVariantPreviews[variant.tempId] ? (
                                                                    <span>No se pudo cargar la imagen. Revisá el enlace y sus permisos.</span>
                                                                ) : (
                                                                    <img loading="lazy" decoding="async"
                                                                        src={previewUrl}
                                                                        alt={`Previsualización de ${variant.NombresProductos || `variante ${index + 1}`}`}
                                                                        onError={() => setFailedVariantPreviews(current => ({ ...current, [variant.tempId]: true }))}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    </article>
                                                );
                                            })}
                                        </div>
                                    )}
                                </section>
                            )}

                            <div className={styles['section-divider']}>Datos de etiqueta</div>

                            <div className={styles['form-group']}>
                                <label>Nombre Etiqueta Freezer</label>
                                <input
                                    type="text"
                                    name="NombreEtiquetaFreezer"
                                    value={formData.NombreEtiquetaFreezer}
                                    onChange={handleChange}
                                    placeholder="Opcional; usa el nombre de etiqueta si queda vacío"
                                />
                            </div>

                            <div className={styles['form-group']}>
                                <label>Porción / peso / unidades</label>
                                <input
                                    type="text"
                                    name="PorcionTexto"
                                    value={formData.PorcionTexto}
                                    onChange={handleChange}
                                    placeholder="Ej.: 500 g, 6 unidades"
                                />
                            </div>

                            <div className={styles['form-group']}>
                                <label>Código de barras para etiqueta</label>
                                <input
                                    type="number"
                                    name="CodigoBarras"
                                    value={formData.CodigoBarras}
                                    onChange={handleChange}
                                    placeholder="Se genera automáticamente al crear"
                                />
                            </div>

                            <div className={styles['form-group']}>
                                <label>Vencimiento predeterminado (días)</label>
                                <input
                                    type="number"
                                    min="1"
                                    name="DiasVencimientoDefault"
                                    value={formData.DiasVencimientoDefault}
                                    onChange={handleChange}
                                    placeholder="Ej.: 365"
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['span-full']}`}>
                                <label>Slug</label>
                                <input
                                    type="text"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleChange}
                                    placeholder="Se genera desde el nombre si se deja vacío al crear"
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['span-full']}`}>
                                <label>Ingredientes</label>
                                <textarea
                                    name="IngredientesSugeridos"
                                    value={formData.IngredientesSugeridos}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Ingredientes del producto..."
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['span-full']}`}>
                                <label>Propiedades tienda</label>
                                <textarea
                                    name="descripcionProducto"
                                    value={formData.descripcionProducto}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Propiedades que se muestran en la tienda web..."
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['span-full']}`}>
                                <label>Propiedades / beneficios etiqueta</label>
                                <textarea
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Propiedades o beneficios para la etiqueta..."
                                />
                            </div>

                            <div
                                className={styles['checkbox-center']}
                                onClick={() => setFormData({ ...formData, EsUltracongelado: !formData.EsUltracongelado })}
                            >
                                <input type="checkbox" checked={!!formData.EsUltracongelado} onChange={() => { }} />
                                <label>Es ultracongelado</label>
                            </div>

                            <div
                                className={styles['checkbox-center']}
                                onClick={() => setFormData({ ...formData, EsEnvasadoAlVacio: !formData.EsEnvasadoAlVacio })}
                            >
                                <input type="checkbox" checked={!!formData.EsEnvasadoAlVacio} onChange={() => { }} />
                                <label>Envasado al vacío</label>
                            </div>

                            <div className={`${styles['form-group']} ${styles['span-full']}`}>
                                <label>Instrucciones de conservación</label>
                                <textarea
                                    name="InstruccionesConservacion"
                                    value={formData.InstruccionesConservacion}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Texto adicional de conservación; las frases estándar se agregan según las opciones anteriores."
                                />
                            </div>

                            <div
                                className={`${styles['checkbox-center']} ${styles['span-full']}`}
                                onClick={() => setFormData({ ...formData, TieneSugerenciasCoccion: !formData.TieneSugerenciasCoccion })}
                            >
                                <input type="checkbox" checked={!!formData.TieneSugerenciasCoccion} onChange={() => { }} />
                                <label>Tiene sugerencias de cocción / regeneración</label>
                            </div>

                            <div className={`${styles['form-group']} ${styles['span-full']}`}>
                                <label>Recomendación de cocción / regeneración</label>
                                <textarea
                                    name="SugerenciasCoccion"
                                    value={formData.SugerenciasCoccion}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Indicaciones de cocción o regeneración..."
                                />
                            </div>

                            {/* Section: Visibilidad */}
                            <div className={styles['section-divider']}>Estado de Visibilidad</div>
                            <div className={styles['checkbox-center']} onClick={() => setFormData({ ...formData, activo: !formData.activo })}>
                                <input
                                    type="checkbox"
                                    name="activo"
                                    checked={!!formData.activo}
                                    onChange={() => { }}
                                />
                                <label style={{ fontWeight: 'bold', color: formData.activo ? '#2e7d32' : '#c62828' }}>
                                    {formData.activo ? 'Producto Visible (Activo)' : 'Producto Oculto (Inactivo)'}
                                </label>
                            </div>
                            <p style={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: '#666', marginTop: '-10px' }}>
                                Los productos ocultos no aparecerán en la web ni en la tabla de stock actual, pero conservarán su historial.
                            </p>

                            <p style={{ gridColumn: '1 / -1', fontSize: '0.82rem', color: '#888', marginTop: '4px', borderTop: '1px dashed #ddd', paddingTop: '10px' }}>
                                Los cambios de esta sección quedan disponibles inmediatamente en la app de Etiquetas porque ambas aplicaciones comparten la misma base de datos.
                            </p>
                        </div>

                        <div className={styles['form-actions']}>
                            <PrimaryButton type="submit">{isEditing ? 'Guardar Cambios' : 'Crear Producto'}</PrimaryButton>
                            {isEditing && (
                                <button
                                    type="button"
                                    onClick={() => { resetForm(); setView('list'); }}
                                    className={styles['btn-secondary']}
                                >
                                    Cancelar
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            ) : (
                <div className={styles['crud-list']}>
                    <div className={styles['search-container']}>
                        <input
                            type="text"
                            placeholder="Buscar producto por nombre o subcategoría..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles['search-input']}
                        />
                    </div>

                    <div className={shared.asyncRegion}>
                        {loading ? <p className={shared.loadingState}>Cargando...</p> : renderProductGrid()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductCrud;
